import { useEffect, useMemo, useState } from "react";
import { apiClient } from "@/App";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronRight, Search } from "lucide-react";
import { errorText } from "@/lib/calendar";
import { StatStrip } from "./StatStrip";
import { fmtDay, planLabel } from "./format";

const Count = ({ n, testid }) => (
  <td className={`px-4 py-3 text-right tabular-nums ${n ? "text-slate-900 font-semibold" : "text-slate-300"}`} data-testid={testid}>{n}</td>
);

const CompanyRow = ({ c, onOpen }) => {
  const key = c.id || "none";
  return (
    <tr className="border-t border-slate-100 hover:bg-lime-50/60 transition-colors" data-testid={`owner-company-row-${key}`}>
      <td className="px-4 py-3">
        <p className="font-semibold text-slate-900">{c.name}</p>
        <p className="text-xs text-slate-500">{c.email || "Accounts that belong to no company"}</p>
      </td>
      <td className="px-4 py-3 text-sm">
        <p className="text-slate-700 whitespace-nowrap">{planLabel(c)}</p>
        {c.is_test && <span className="inline-block mt-1 whitespace-nowrap text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">Test / demo</span>}
      </td>
      <Count n={c.accounts.dispatchers} testid={`owner-company-dispatchers-${key}`} />
      <Count n={c.accounts.drivers} />
      <Count n={c.accounts.customers} />
      <Count n={c.rides} testid={`owner-company-rides-${key}`} />
      <td className={`px-4 py-3 text-right tabular-nums ${c.accounts.suspended ? "text-red-600 font-bold" : "text-slate-300"}`}>{c.accounts.suspended}</td>
      <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{fmtDay(c.created_at)}</td>
      <td className="px-4 py-3 text-right">
        <Button size="sm" variant="ghost" onClick={() => onOpen(c)} className="text-indigo-900 hover:bg-indigo-50" data-testid={`owner-company-open-${key}`}>
          Accounts <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </td>
    </tr>
  );
};

export const CompaniesPanel = ({ onOpen }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [hideTest, setHideTest] = useState(false);

  useEffect(() => {
    apiClient.get("/owner/companies").then((r) => setData(r.data)).catch((e) => toast.error(errorText(e, "Could not load companies")));
  }, []);

  const rows = useMemo(() => (data?.companies || []).filter((c) =>
    (!hideTest || !c.is_test) && (!q || `${c.name} ${c.email || ""}`.toLowerCase().includes(q.toLowerCase()))), [data, q, hideTest]);

  if (!data) return <p className="text-slate-500 py-12" data-testid="owner-companies-loading">Loading companies…</p>;

  return (
    <div data-testid="owner-companies-panel">
      <StatStrip totals={data.totals} />
      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies" className="pl-9 bg-white" data-testid="owner-companies-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
          <Switch checked={hideTest} onCheckedChange={setHideTest} data-testid="owner-hide-test-switch" /> Hide test & demo companies
        </label>
        <span className="text-sm text-slate-400 sm:ml-auto" data-testid="owner-companies-count">{rows.length} shown</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3 text-left">Company</th><th className="px-4 py-3 text-left">Plan</th>
              <th className="px-4 py-3 text-right">Dispatch</th><th className="px-4 py-3 text-right">Drivers</th>
              <th className="px-4 py-3 text-right">Customers</th><th className="px-4 py-3 text-right">Rides</th>
              <th className="px-4 py-3 text-right">Suspended</th><th className="px-4 py-3 text-left">Created</th><th />
            </tr>
          </thead>
          <tbody>{rows.map((c) => <CompanyRow key={c.id || "none"} c={c} onOpen={onOpen} />)}</tbody>
        </table>
      </div>
    </div>
  );
};
